import React, { Fragment } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Colors, Icon, Icons, Loader, LoaderSizes } from "@sellout/ui";
import * as Time from "@sellout/utils/.dist/time";
import * as AppActions from "../redux/actions/app.actions";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { BackstageState } from "../redux/store";
import EventUtil from "@sellout/models/.dist/utils/EventUtil";
import { ModalTypes } from "./modal/Modal";
import IGuestListEntry from "@sellout/models/.dist/interfaces/IGuestListEntry";
import { IEventGraphQL } from "@sellout/models/.dist/interfaces/IEvent";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "./ScrollableTable";
import { NoContentHead } from "./OrdersTable";

/************************************************************
 * Enums
 ************************************************************/

export enum GuestListTableText {
  NoGuests = "No guests added yet",
  ColName = "Name",
  ColCreated = "Created Date/Time",
  ColCheckedIn = "Checked-In Date/Time",
  ColActions = "Actions",
  DeleteTitle = "Delete Guest",
  DeleteMessage = "Are you sure you want to remove {name} from the guest list?",
  DeleteConfirm = "DELETE",
  DeleteCancel = "CANCEL",
  CannotDeleteCheckedIn = "{name} has already been checked-in and can't be removed from the guest list.",
  AlreadyCheckedIn = "{name} has already been checked in.",
  InfoConfirm = "OKAY",
  CheckInTitle = "Check In Guest",
  CheckInMessage = "Do you want to check in {name} to this event?",
  CheckInConfirm = "CONFIRM",
  CheckInCancel = "CANCEL",
}

export enum GuestListColFlex {
  Name = "1",
  DateTime = "2",
  Actions = "1",
}

/************************************************************
 * Styled Components
 ************************************************************/

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionIcon = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  * {
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  }
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.White};
  border-radius: 0;
  padding: 10px 0px;
`;

/************************************************************
 * TableHeader
 ************************************************************/

const TableHeader: React.FC = () => {
  return (
    <ScrollTableHeader>
      <ScrollTableHeaderCell flex={GuestListColFlex.Name}>
        {GuestListTableText.ColName}
      </ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell flex={GuestListColFlex.DateTime}>
        {GuestListTableText.ColCreated}
      </ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell flex={GuestListColFlex.DateTime}>
        {GuestListTableText.ColCheckedIn}
      </ScrollTableHeaderCell>
      <ScrollTableSpace />
      <ScrollTableHeaderCell flex={GuestListColFlex.Actions}>
        {GuestListTableText.ColActions}
      </ScrollTableHeaderCell>
    </ScrollTableHeader>
  );
};

/************************************************************
 * TableRows
 ************************************************************/

type TableRowsProps = {
  guestList: IGuestListEntry[];
  event?: IEventGraphQL;
  refetch?: Function;
};

const TableRows: React.FC<TableRowsProps> = ({ guestList, event, refetch }) => {
  const dispatch = useDispatch();
  const eventState = useSelector((state: BackstageState) => state.event);
  const { eventId } = eventState;
  const timezone =
    event?.venue?.address?.timezone || "America/Denver";
  const currentDayStartsAt = event
    ? EventUtil.currentGuestListDay(event, timezone)
    : null;
  const isCancelled = Boolean(event?.cancel);
  // currentGuestListDay already resolves a session that is running past
  // midnight, so it is the same condition the server checks. Falling back to
  // isInProgress here used to offer a check-in the server would then reject.
  const isDayOfEvent = !isCancelled && currentDayStartsAt !== null;

  const handleDelete = (guest: IGuestListEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    if (guest.checkIns?.some((c) => c.checkedIn)) return;
    dispatch(
      AppActions.pushModal(ModalTypes.DeleteGuestModal, {
        guest,
        refetch,
      })
    );
  };

  const handleCheckIn = (
    guest: IGuestListEntry,
    isAlreadyCheckedIn: boolean,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (isCancelled) return;
    const fullName = `${guest.firstName} ${guest.lastName}`;

    // Already checked in today — show info alert
    if (isAlreadyCheckedIn) {
      dispatch(
        AppActions.pushModalConfirmAction({
          title: GuestListTableText.CheckInTitle,
          message: GuestListTableText.AlreadyCheckedIn.replace(
            "{name}",
            fullName
          ),
          confirm: () => dispatch(AppActions.popModal()),
          confirmText: GuestListTableText.InfoConfirm,
          cancel: null,
        })
      );
      return;
    }

    dispatch(
      AppActions.pushModal(ModalTypes.CheckInGuestModal, {
        guest,
      })
    );
  };

  const sortedGuestList = React.useMemo(() => {
    if (!guestList?.length) return [];
    return [...guestList].sort((a, b) => {
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim();
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
  }, [guestList]);

  return (
    <>
      {sortedGuestList.map((guest: IGuestListEntry) => {
        const fullName = `${guest.firstName} ${guest.lastName}`;
        const createdDate = guest.createdAt
          ? Time.format(
              guest.createdAt,
              "ddd, MMM DD, YYYY [at] h:mma",
              timezone
            )
          : "-";

        // Find today's check-in or latest check-in (Multi-day events)
        const todayCheckIn = guest.checkIns?.find(
          (c) => c.startsAt === currentDayStartsAt
        );
        const latestCheckIn = guest.checkIns
          ?.filter((c) => c.checkedIn && c.checkedInAt)
          ?.sort((a, b) => (b.checkedInAt || 0) - (a.checkedInAt || 0))[0];

        const activeCheckIn = todayCheckIn?.checkedIn
          ? todayCheckIn
          : latestCheckIn;

        const checkedInDate = activeCheckIn?.checkedInAt
          ? Time.format(
              activeCheckIn.checkedInAt,
              "ddd, MMM DD, YYYY [at] h:mma",
              timezone
            )
          : "-";

        const isCheckedIn = Boolean(guest.checkIns?.some((c) => c.checkedIn));
        const isCheckedInToday = Boolean(todayCheckIn?.checkedIn);

        return (
          <ScrollTableBodyRow key={guest._id} height="40px">
            <ScrollTableBodyCell flex={GuestListColFlex.Name}>
              {fullName}
            </ScrollTableBodyCell>
            <ScrollTableSpace />

            <ScrollTableBodyCell flex={GuestListColFlex.DateTime}>
              {createdDate}
            </ScrollTableBodyCell>
            <ScrollTableSpace />

            <ScrollTableBodyCell flex={GuestListColFlex.DateTime}>
              {checkedInDate}
            </ScrollTableBodyCell>
            <ScrollTableSpace />

            <ScrollTableBodyCell flex={GuestListColFlex.Actions}>
              <IconWrapper>
                {isDayOfEvent && (
                  <Icon
                    icon={Icons.TicketSolid}
                    color={
                      isCheckedInToday ? Colors.Grey1 : Colors.Orange
                    }
                    size={16}
                    onClick={(e: React.MouseEvent) =>
                      handleCheckIn(guest, isCheckedInToday, e)
                    }
                  />
                )}
                <ActionIcon
                  disabled={isCheckedIn}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (isCheckedIn) {
                      dispatch(
                        AppActions.showNotification(
                          GuestListTableText.CannotDeleteCheckedIn.replace(
                            "{name}",
                            fullName
                          ),
                          AppNotificationTypeEnum.Warning
                        )
                      );
                      return;
                    }
                    handleDelete(guest, e);
                  }}
                >
                  <Icon
                    icon={Icons.DeleteRegular}
                    color={isCheckedIn ? Colors.Grey3 : Colors.Red}
                    size={16}
                  />
                </ActionIcon>
              </IconWrapper>
            </ScrollTableBodyCell>
          </ScrollTableBodyRow>
        );
      })}
    </>
  );
};

/************************************************************
 * GuestListTable
 ************************************************************/

type GuestListTableProps = {
  guestList: IGuestListEntry[];
  event?: IEventGraphQL;
  refetch?: Function;
  loading?: boolean;
};

const GuestListTable: React.FC<GuestListTableProps> = ({
  guestList,
  event,
  refetch,
  loading,
}) => {
  const scrollContainer = React.useRef<any>(null);

  if (loading) {
    return (
      <ScrollTable>
        <TableHeader />
        <ScrollTableBody>
          <LoaderContainer>
            <Loader size={LoaderSizes.Large} color={Colors.Orange} />
          </LoaderContainer>
        </ScrollTableBody>
      </ScrollTable>
    );
  }

  if (!guestList || guestList.length <= 0) {
    return (
      <ScrollTable>
        <TableHeader />
        <ScrollTableBody>
          <ScrollTableNoContent>
            <NoContentHead>{GuestListTableText.NoGuests}</NoContentHead>
          </ScrollTableNoContent>
        </ScrollTableBody>
      </ScrollTable>
    );
  }

  return (
    <ScrollTable>
      {(tableLoading: boolean) => {
        return (
          <Fragment>
            <TableHeader />
            <ScrollTableBody ref={scrollContainer}>
              <TableRows
                guestList={guestList}
                event={event}
                refetch={refetch}
              />
              {(loading || tableLoading) && (
                <LoaderContainer>
                  <Loader size={LoaderSizes.Large} color={Colors.Orange} />
                </LoaderContainer>
              )}
            </ScrollTableBody>
          </Fragment>
        );
      }}
    </ScrollTable>
  );
};

export default GuestListTable;
